import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import PageContainer from '../layout/PageContainer';
import { Alert } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridEventListener,
  gridClasses,
} from '@mui/x-data-grid';
import api from '../../api/axiosInstance';
import type { Order } from '../../models/Order';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

const INITIAL_PAGE_SIZE = 100;

export default function OrderList() {
  const navigate = useNavigate();

  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadData = useCallback(async () => {
    setError(null);

    try {
      const response = await api.get('/orders');
      setOrders(response.data);
    } catch (showDataError) {
      setError(showDataError as Error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRowClick = useCallback<GridEventListener<'rowClick'>>(
    ({ row }) => {
      navigate(`/dashboard/orders/${row.id}`);
    },
    [navigate],
  );

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'source',
        headerName: 'Source',
        flex: .5,
        display: 'flex'
      },
      {
        field: 'customerEmail',
        headerName: 'Customer Email',
        flex: .5,
        display: 'flex'
      },
      {
        field: 'orderNumber',
        headerName: 'Order Number',
        flex: .5,
        display: 'flex'
      },
      {
        field: 'fulfilledOn',
        headerName: 'Fulfilled On',
        type: 'dateTime',
        flex: 1,
        display: 'flex',
        valueFormatter: (value) => dayjs(value).format('M/D/YYYY h:mm A')
      }
    ],
    [],
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          m: 1,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <Alert severity="error">{error.message}</Alert>
      </Box>
    );
  }

  const pageTitle = 'Orders';

  return (
    <PageContainer
      title={pageTitle}
      breadcrumbs={[
        { title: 'Orders' }
      ]}
    >
      <Box
        sx={{ width: '100%' }}
      >
        <Stack spacing={2} justifyContent="space-between" direction="row" sx={{ width: '100%' }}>
          <Box sx={{ height: '64vh', width: '100%' }}>
            <DataGrid
              density="compact"
              rows={orders}
              columns={columns}
              loading={isLoading}
              pageSizeOptions={[INITIAL_PAGE_SIZE]}
              onRowClick={handleRowClick}
              getRowHeight={() => 'auto'}
              disableRowSelectionOnClick
              sx={[
                () => ({
                  '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
                  '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '15px' },
                  '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '22px' },
                  [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
                    outline: 'transparent',
                  },
                  [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
                  {
                    outline: 'none',
                  },
                  [`& .${gridClasses.row}:hover`]: {
                    cursor: 'pointer',
                  }
                }),
              ]}
              slotProps={{
                loadingOverlay: {
                  variant: 'circular-progress',
                  noRowsVariant: 'circular-progress',
                },
                baseIconButton: {
                  size: 'small',
                },
              }}
            />
          </Box>
        </Stack>
      </Box >
    </PageContainer >
  );
}
